import clsx from 'clsx';
import * as React from 'react';
import {
    type ColorValue,
    type NativeSyntheticEvent,
    type StyleProp,
    StyleSheet,
    TextInput,
    type TextInputKeyPressEventData,
    type TextInputProps,
    View,
    type ViewStyle,
} from 'react-native';

import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import { Description, Label } from '@gitbook/spine-react';
import { useStateWithDeps } from '@gitbook/util-react-hooks';

import { sizes, spacing } from '../constants';
import { Tag } from '../Tags';
import { ThemedStyleSheet, themedFonts, useCurrentTheme } from '../theme';
import type { TextInputFeedback, TextInputRef } from './TextInput';

export type TagInputProps<Tag = string> = Omit<TextInputProps, 'value' | 'onChangeValue'> & {
    label?: React.ReactNode;
    labelDescription?: React.ReactNode;
    labelSelectable?: boolean;
    value: Tag[];
    text?: string;
    style?: StyleProp<ViewStyle>;
    onEnterTag?: (text: string) => void;
    onChangeValue: (tags: Tag[]) => void | React.Dispatch<React.SetStateAction<Tag[]>>;
    renderTag?: (tag: Tag) => string;
    disabled?: boolean;
    feedback?: TextInputFeedback;

    /**
     * Key codes that will trigger the onEnterTag callback.
     */
    tagDelimiters?: string[];

    /** Button to show on the right side of the input */
    button?: React.ReactNode;

    /**
     * React ref to the wrapper view.
     */
    wrapperRef?: React.Ref<View>;
};

/**
 * A hook that can be used to take into account the remaining of the text in the TagInput
 * It returns the props to set on the TagInput as well as the value + the text (if validated)
 */
export function useTagInputState<Tag = string>(
    options: {
        validateTag?: (newText: string) => boolean;
        maxTags?: number;
        getTag?: (newText: string) => Tag;
        initialState?: Tag[];
    } = {}
) {
    const {
        validateTag = (newText: string) => !!newText,
        maxTags,
        getTag = (newText: string) => newText as Tag,
        initialState = [],
    } = options;
    const [value, setValue] = useStateWithDeps<Tag[]>(initialState, initialState);
    const [text, setText] = React.useState('');
    const [feedback, setFeedback] = React.useState<TextInputFeedback | undefined>();

    const onSaveTags = React.useCallback(
        (newTags: Tag[]) => {
            setFeedback(undefined);

            if (maxTags && newTags.length > maxTags) {
                setFeedback({
                    type: 'danger',
                    message: `Too many items entered, please enter at most ${maxTags} items.`,
                });
                return;
            }

            if (newTags) {
                setValue(newTags);
            }

            setText('');
        },
        [setValue, maxTags]
    );

    const addTags = React.useCallback(
        (tags: Tag[]) => {
            setValue((value) => {
                if (maxTags && value.length + tags.length > maxTags) {
                    setFeedback({
                        type: 'danger',
                        message: `Too many items entered, please enter at most ${maxTags} items.`,
                    });
                    return value;
                }

                return value.concat(tags);
            });
        },
        [setValue, maxTags]
    );

    const onEnterTag = React.useCallback(
        (newText: string) => {
            setFeedback(undefined);

            if (validateTag(newText)) {
                addTags([getTag(newText)]);
            }

            setText('');
        },
        [validateTag, addTags, getTag]
    );

    const onChangeText = React.useCallback(
        (input: string) => {
            // Handle potentially pasted content
            const parts = input.split(/[\s,;]+/);
            const tags: Tag[] = [];

            const newText = parts.pop() || '';

            for (const part of parts) {
                if (validateTag(part)) {
                    tags.push(getTag(part));
                }
            }

            if (tags.length) {
                addTags(tags);
            }

            setText(newText);
        },
        [validateTag, getTag, addTags]
    );

    const valueAndRemainingText = validateTag?.(text) ? value.concat([getTag(text)]) : value;

    const reset = () => {
        setValue(initialState);
        setText('');
        setFeedback(undefined);
    };

    return [
        { onChangeValue: onSaveTags, value, feedback, text, onChangeText, onEnterTag },
        valueAndRemainingText,
        { reset },
    ] as const;
}

export type TagInputRef = TextInputRef;

const defaultTagDelimiters = [',', ' ', ';', 'Enter', 'Tab'];

/**
 * Input to insert tags.
 */
export const TagInput = React.forwardRef<TagInputRef, TagInputProps<any>>(function TagInputWithRef<
    Tag = string,
>(props: TagInputProps<Tag>, ref: React.Ref<TextInput>): React.ReactElement {
    const [theme, font] = useCurrentTheme(themedFonts);
    const [defaultText, setDefaultText] = React.useState('');
    const {
        label,
        labelDescription,
        labelSelectable,
        value,
        text = defaultText,
        style,
        onChangeValue,
        onChangeText = setDefaultText,
        onEnterTag = (newText: string) => {
            if (newText) {
                // @ts-ignore
                props.onChangeValue(value.concat([newText]));
            }

            onChangeText('');
        },
        renderTag = (tag: Tag) => String(tag),
        disabled,
        placeholder,
        feedback,
        wrapperRef,
        button,
        tagDelimiters = defaultTagDelimiters,
        ...inputProps
    } = props;
    const [focused, setFocused] = React.useState(false);
    const [, resolvedStyles] = useCurrentTheme(themesStyles);

    const onRemoveTag = (index: number) => {
        if (disabled) {
            return;
        }
        const newValue = [...value];
        newValue.splice(index, 1);
        onChangeValue(newValue);
    };

    const onKeyPress = (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        if (disabled) {
            return;
        }
        if (value.length > 0 && text.length === 0 && event.nativeEvent.key === 'Backspace') {
            onRemoveTag(value.length - 1);
            return;
        }

        if (onEnterTag && tagDelimiters.includes(event.nativeEvent.key)) {
            onEnterTag(text);

            if (text) {
                // prevent bluring the input when pressing Enter or Tab
                event.preventDefault();
            }
        }
    };

    let FeedbackIcon: SVGIconComponent | undefined;
    let feedbackStyle: string | undefined;
    if (feedback) {
        if (feedback.type === 'success') {
            FeedbackIcon = Icon.Check;
            feedbackStyle = 'text-success';
        } else if (feedback.type === 'info') {
            feedbackStyle = 'text-primary';
        } else if (feedback.type === 'danger') {
            FeedbackIcon = Icon.Alert;
            feedbackStyle = 'text-danger';
        }
    }

    return (
        <View style={style}>
            {label ? (
                <View style={[spacing.Margin.bottom200]}>
                    <Label className={labelSelectable ? 'select-all' : undefined}>{label}</Label>
                    {labelDescription ? (
                        <View style={spacing.Margin.top100}>
                            <Description className={labelSelectable ? 'select-all' : undefined}>
                                {labelDescription}
                            </Description>
                        </View>
                    ) : null}
                </View>
            ) : null}
            <View
                ref={wrapperRef}
                style={[
                    styles.tagInput,
                    value.length > 0 && styles.tagInputWithTags,
                    resolvedStyles.tagInput,
                    focused && resolvedStyles.tagInputFocused,
                    feedback &&
                        feedback.type === 'danger' && {
                            borderColor: theme.colors.danger.main as ColorValue,
                        },
                ]}
            >
                {value.map((tag, index) => (
                    <Tag
                        key={index}
                        onPress={() => onRemoveTag(index)}
                        deletable
                        style={styles.tag}
                        size="small"
                    >
                        {renderTag(tag)}
                    </Tag>
                ))}
                <TextInput
                    ref={ref}
                    {...inputProps}
                    placeholder={!value.length ? placeholder : undefined}
                    placeholderTextColor={theme.colors.text.light}
                    value={text}
                    onChangeText={onChangeText}
                    onKeyPress={onKeyPress}
                    onFocus={(e) => {
                        if (disabled) {
                            return;
                        }
                        setFocused(true);
                        if (inputProps.onFocus) {
                            inputProps.onFocus(e);
                        }
                    }}
                    onBlur={(e) => {
                        if (disabled) {
                            return;
                        }
                        setFocused(false);
                        if (inputProps.onBlur) {
                            inputProps.onBlur(e);
                        }
                    }}
                    style={[font.Label, styles.textInput, resolvedStyles.tagTextInput]}
                />
                {FeedbackIcon && (
                    <FeedbackIcon size={sizes.ICON_400} className={clsx('mr-4', feedbackStyle)} />
                )}
                {button ? <View style={styles.tagInputButton}>{button}</View> : null}
            </View>
            {feedback && feedback.type !== 'none' && typeof feedback.message !== 'undefined' && (
                <div className={clsx('pt-2 text-ui-small', feedbackStyle)}>{feedback.message}</div>
            )}
        </View>
    );
});

const styles = StyleSheet.create({
    tagInput: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        flex: 1,
        paddingHorizontal: spacing.small,
        paddingVertical: spacing.small,
        borderWidth: 1,
        borderRadius: sizes.RADIUS_M,
        minHeight: sizes.BUTTON_M,
    },
    tagInputWithTags: {
        paddingVertical: spacing.xxsmall,
    },
    textInput: {
        flex: 1,
        outlineStyle: 'none',
        marginHorizontal: spacing.small,
    },
    tag: {
        margin: spacing.xxsmall,
    },
    tagInputButton: {
        // We want to align as much as possible the button on the right side
        marginLeft: 'auto',
    },
});

const themesStyles = ThemedStyleSheet.create({
    tagInput: (theme) => ({
        borderColor: theme.colors.border.main,
        backgroundColor: theme.colors.background.main,
    }),
    tagInputFocused: (theme) => ({
        borderColor: theme.colors.primary.main,
    }),
    tagTextInput: (theme) => ({
        color: theme.colors.text.main,
    }),
});
